import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Slim top-of-page scroll progress bar + (Home only) section indicator chip.
 * Tells visitors how much of the page is left and what section they're in.
 */
export default function ScrollProgress() {
  const { pathname } = useLocation();
  const [progress, setProgress] = useState(0);
  const [section, setSection] = useState({ label: "", idx: 0, total: 0 });

  useEffect(() => {
    setProgress(0);
    setSection({ label: "", idx: 0, total: 0 });
  }, [pathname, setProgress, setSection]);

  useEffect(() => {
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.max(0, (scrollTop / docHeight) * 100)) : 0;
      setProgress(pct);

      // Only compute section labels on Home
      if (pathname !== "/") {
        setSection({ label: "", idx: 0, total: 0 });
        return;
      }

      const sections = [
        { sel: "[data-testid=hero]", label: "Made for little adventures" },
        { sel: "[data-testid=our-story]", label: "Our story" },
        { sel: "[data-testid=featured-rail]", label: "Featured frames" },
        { sel: "[data-testid=collection-grid]", label: "Shop the collection" },
        { sel: "[data-testid=personality-grid]", label: "By personality" },
        { sel: "[data-testid=quiz-teaser]", label: "Find their pair" },
        { sel: "[data-testid=in-action]", label: "Out in the world" },
        { sel: "[data-testid=reviews]", label: "Parent reviews" },
        { sel: "[data-testid=why-sol]", label: "Why SOL" },
        { sel: "[data-testid=faq]", label: "Questions" },
      ];
      const probe = window.innerHeight * 0.35; // line ~35% from top of viewport
      let active = 0;
      for (let i = 0; i < sections.length; i++) {
        const el = document.querySelector(sections[i].sel);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= probe) active = i;
      }
      setSection({ label: sections[active].label, idx: active + 1, total: sections.length });
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [pathname]);

  const showChip = pathname === "/" && progress > 4 && progress < 96;

  return (
    <>
      {/* Top progress bar — sits above everything */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none"
        aria-hidden
        data-testid="scroll-progress-bar"
      >
        <div
          className="h-full transition-[width] duration-150 ease-out"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #8BC547 0%, #F4C734 50%, #DD2E20 100%)",
          }}
        />
      </div>

      {/* Floating section chip — Home only, fades in after scrolling past hero */}
      <div
        className={`fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[55] pointer-events-none transition-all duration-300 ${
          showChip ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
        }`}
        data-testid="section-indicator-chip"
      >
        <div className="rounded-full bg-[#2C2C2A]/92 backdrop-blur-sm text-[#FAFAF7] px-3.5 py-2 flex items-center gap-2 shadow-[0_8px_24px_rgba(0,0,0,0.18)]">
          <span className="text-[10px] font-medium tabular-nums tracking-[0.15em] opacity-70">
            {String(section.idx).padStart(2, "0")} / {String(section.total).padStart(2, "0")}
          </span>
          <span className="w-px h-3 bg-[#FAFAF7]/25" aria-hidden />
          <span className="text-[12px] font-medium tracking-[0.01em] max-w-[180px] truncate">
            {section.label}
          </span>
        </div>
      </div>
    </>
  );
}
